import { getLobLabel } from '@/app/config/products.config';
import type {
  PolicyBatchFacets,
  PolicyBatchItem,
} from '@/services/integrations';

export interface PolicyBatchOption {
  label: string;
  value: string;
}

/** Chave única de um item do balde — mesma `PolicyBatchKey` que o BFF usa (PK + SK). */
export function buildPolicyBatchKey(
  item: Pick<PolicyBatchItem, 'lobId' | 'policyNumber'>
): string {
  return `${item.lobId}#${item.policyNumber}`;
}

/** `14 — Residencial`; ramo sem produto cadastrado fica só com o número. */
export function formatLobOption(lobId: number): string {
  const label = getLobLabel(lobId);
  return label ? `${lobId} — ${label}` : String(lobId);
}

function toOptions(
  values: number[] | undefined,
  format: (value: number) => string
): PolicyBatchOption[] {
  return (values ?? []).map(value => ({
    value: String(value),
    label: format(value),
  }));
}

export function buildLobOptions(
  facets?: PolicyBatchFacets
): PolicyBatchOption[] {
  return toOptions(facets?.lobIds, formatLobOption);
}

export function buildChannelCodeOptions(
  facets?: PolicyBatchFacets
): PolicyBatchOption[] {
  return toOptions(facets?.channelCodes, String);
}

export function buildBatchIdOptions(
  facets?: PolicyBatchFacets
): PolicyBatchOption[] {
  return toOptions(facets?.batchIds, String);
}

/** Adiciona "Todos" no topo — usado nos filtros da listagem, não nos modais de edição. */
export function withAllOption(
  options: PolicyBatchOption[]
): PolicyBatchOption[] {
  return [{ label: 'Todos', value: '' }, ...options];
}

/**
 * Garante que o valor em uso apareça na lista.
 *
 * Os facets trazem só o que existe no balde HOJE. Um valor vindo da URL (link
 * compartilhado, filtro salvo) ou já gravado num registro pode não estar lá — sem isso o
 * combobox mostraria o placeholder enquanto o filtro segue ativo.
 */
export function withCurrentOption(
  options: PolicyBatchOption[],
  current: string
): PolicyBatchOption[] {
  if (!current || options.some(option => option.value === current)) {
    return options;
  }
  return [...options, { value: current, label: current }];
}
